import { defineStore } from 'pinia'

interface User {
  id: string
  email: string
  provider?: string
  permissions?: string[]
}

interface SessionResponse {
  authenticated: boolean
  user?: User
  message?: string
}

interface SessionState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingOut: boolean
  error: string | null
}

export const useSessionStore = defineStore('session', {
  state: (): SessionState => ({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isLoggingOut: false,
    error: null,
  }),

  actions: {
    async checkSession() {
      this.isLoading = true
      this.error = null

      try {
        const response = await fetch('/api/session', {
          headers: {
            Accept: 'application/json',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error(`Session check failed: ${response.status}`)
        }

        const data = (await response.json()) as SessionResponse

        if (data.authenticated && data.user) {
          this.user = data.user
          this.isAuthenticated = true
        } else {
          this.isAuthenticated = false
          this.user = null
          throw new Error(data.message || 'Authentication failed')
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch session'
        this.isAuthenticated = false
        this.user = null
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async fetchSession() {
      await this.checkSession()
    },

    async logout() {
      if (this.isLoggingOut) {
        return false
      }
      this.isLoggingOut = true

      try {
        const response = await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        if (response.ok) {
          const result = await response.json()

          if (result.logoutUrl) {
            this.clearSession()
            window.location.href = result.logoutUrl
            return true
          }
        }
      } catch (error) {
        console.error('Logout API error:', error)
      }

      this.clearSession()
      window.location.href = '/login'

      return true
    },

    clearSession() {
      this.user = null
      this.isAuthenticated = false
      this.error = null
    },

    /**
     * Check if user has a specific permission
     *
     * @param permission - Permission string to check
     * @returns True if user has permission
     *
     * Note: Returns true if no permissions defined (backward compatibility)
     */
    hasPermission(permission: string): boolean {
      if (!this.isAuthenticated || !this.user) {
        return false
      }

      if (!this.user.permissions) {
        return true
      }

      return this.user.permissions.includes(permission)
    },
  },
})
